import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const JURISDICTIONS = ['England & Wales', 'Cayman Islands', 'Luxembourg', 'Ireland', 'Mauritius', 'Singapore', 'Other'];
const ENTITY_TYPES = ['Fund', 'HoldCo', 'SPV', 'ProjectCo', 'JV'];
const INCORP_STATUS = ['Proposed', 'Incorporated', 'Active'];

const INITIAL_ENTITIES = [
  { id: 1, name: 'SeedDrive Infrastructure Fund III', type: 'Fund', jurisdiction: 'Cayman Islands', status: 'Active', ownershipPct: 100, taxNotes: 'Cayman exempted LP', agent: 'Maples Group' },
  { id: 2, name: 'SD Mersey HoldCo Ltd', type: 'HoldCo', jurisdiction: 'England & Wales', status: 'Incorporated', ownershipPct: 100, taxNotes: 'UK resident, group relief available', agent: 'TMF Group' },
  { id: 3, name: 'Mersey Tidal Array SPV Ltd', type: 'SPV', jurisdiction: 'England & Wales', status: 'Proposed', ownershipPct: 100, taxNotes: 'Ring-fenced project vehicle', agent: 'TBC' },
];

const INITIAL_CPS = [
  { id: 1, item: 'Planning consent confirmation', party: 'Sponsor', dueDate: '15 Apr 2026', status: 'Outstanding' },
  { id: 2, item: 'Environmental impact assessment sign-off', party: 'Legal Counsel', dueDate: '30 Apr 2026', status: 'Outstanding' },
  { id: 3, item: 'Grid connection agreement executed', party: 'Sponsor', dueDate: '01 May 2026', status: 'Satisfied' },
  { id: 4, item: 'Financial model audit completed', party: 'Financial Adviser', dueDate: '20 Apr 2026', status: 'Outstanding' },
  { id: 5, item: 'Lender credit committee approval', party: 'Debt Arranger', dueDate: '10 May 2026', status: 'Outstanding' },
];

const CP_STATUS_CONFIG = {
  Outstanding: 'bg-amber-50 text-amber-700',
  Satisfied: 'bg-emerald-50 text-emerald-700',
  Waived: 'bg-slate-100 text-slate-600',
};

const INCORP_CONFIG = {
  Proposed: 'bg-slate-100 text-slate-600',
  Incorporated: 'bg-[rgba(59,76,76,0.07)] text-blue-700',
  Active: 'bg-emerald-50 text-emerald-700',
};

export default function LegalStructureTab({ deal, readOnly }) {
  const [entities, setEntities] = useState(INITIAL_ENTITIES);
  const [cps, setCps] = useState(INITIAL_CPS);
  const [governingLaw, setGoverningLaw] = useState('England & Wales');
  const [disputeResolution, setDisputeResolution] = useState('LCIA');
  const [warranties, setWarranties] = useState({
    title: true, planning: true, environmental: true, financialModel: true, mac: false,
  });

  const updateCpStatus = (id, status) => {
    if (readOnly) return;
    setCps((prev) => prev?.map((cp) => (cp?.id === id ? { ...cp, status } : cp)));
  };

  return (
    <div className="space-y-6">
      {/* Entity Structure Diagram */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Network" size={16} color="var(--color-primary)" />
          Legal Entity Structure
        </h3>
        <div className="flex flex-col items-center gap-2 py-4">
          {entities?.map((entity, idx) => (
            <div key={entity?.id} className="flex flex-col items-center">
              <div className="border-2 border-border rounded-xl p-4 bg-muted/20 w-72 text-center shadow-sm">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-foreground">{entity?.name}</span>
                </div>
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  <span className="text-xs bg-[rgba(59,76,76,0.07)] text-blue-700 px-2 py-0.5 rounded-full">{entity?.type}</span>
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{entity?.jurisdiction}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${INCORP_CONFIG?.[entity?.status] || 'bg-slate-100 text-slate-600'}`}>{entity?.status}</span>
                </div>
                {entity?.ownershipPct < 100 && (
                  <p className="text-xs text-muted-foreground mt-1">{entity?.ownershipPct}% ownership</p>
                )}
              </div>
              {idx < entities?.length - 1 && (
                <div className="flex flex-col items-center">
                  <div className="w-px h-6 bg-border" />
                  <Icon name="ChevronDown" size={16} color="var(--color-muted-foreground)" />
                  <div className="w-px h-2 bg-border" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Entity List */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Building2" size={16} color="var(--color-primary)" />
          Entity Details
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                {['Entity Name', 'Type', 'Jurisdiction', 'Status', 'Ownership %', 'Tax Notes', 'Registered Agent']?.map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {entities?.map((e) => (
                <tr key={e?.id} className="hover:bg-muted/20">
                  <td className="px-3 py-2 font-medium text-foreground">{e?.name}</td>
                  <td className="px-3 py-2 text-muted-foreground">{e?.type}</td>
                  <td className="px-3 py-2 text-muted-foreground">{e?.jurisdiction}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${INCORP_CONFIG?.[e?.status] || 'bg-slate-100 text-slate-600'}`}>{e?.status}</span>
                  </td>
                  <td className="px-3 py-2">{e?.ownershipPct}%</td>
                  <td className="px-3 py-2 text-muted-foreground text-xs">{e?.taxNotes}</td>
                  <td className="px-3 py-2 text-muted-foreground text-xs">{e?.agent}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Key Legal Terms */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Scale" size={16} color="var(--color-primary)" />
            Key Legal Terms
          </h3>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-muted-foreground">Governing Law</label>
              <select value={governingLaw} onChange={(e) => !readOnly && setGoverningLaw(e?.target?.value)} disabled={readOnly}
                className="w-full mt-1 px-3 py-1.5 text-sm border border-border rounded-lg bg-card text-foreground focus:outline-none disabled:opacity-60">
                {JURISDICTIONS?.map((j) => <option key={j} value={j}>{j}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Dispute Resolution</label>
              <select value={disputeResolution} onChange={(e) => !readOnly && setDisputeResolution(e?.target?.value)} disabled={readOnly}
                className="w-full mt-1 px-3 py-1.5 text-sm border border-border rounded-lg bg-card text-foreground focus:outline-none disabled:opacity-60">
                {['LCIA', 'ICC', 'LMAA', 'Courts']?.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-2 block">Key Warranties & Representations</label>
              <div className="space-y-2">
                {Object.entries(warranties)?.map(([key, val]) => (
                  <label key={key} className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                    <input type="checkbox" checked={val} onChange={(e) => !readOnly && setWarranties((w) => ({ ...w, [key]: e?.target?.checked }))} disabled={readOnly}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary/30" />
                    {key === 'title' ? 'Title & ownership' : key === 'planning' ? 'Planning & permits' : key === 'environmental' ? 'Environmental compliance' : key === 'financialModel' ? 'Financial model accuracy' : 'Material Adverse Change (MAC)'}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Conditions Precedent */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="CheckSquare" size={16} color="var(--color-primary)" />
            Conditions Precedent
          </h3>
          <div className="space-y-2">
            {cps?.map((cp) => (
              <div key={cp?.id} className="flex items-start gap-3 p-3 rounded-lg border border-border bg-muted/20">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{cp?.item}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{cp?.party} · Due {cp?.dueDate}</p>
                </div>
                <select
                  value={cp?.status}
                  onChange={(e) => updateCpStatus(cp?.id, e?.target?.value)}
                  disabled={readOnly}
                  className={`text-xs px-2 py-1 rounded-full border-0 font-semibold focus:outline-none disabled:opacity-60 ${CP_STATUS_CONFIG?.[cp?.status] || 'bg-slate-100 text-slate-600'}`}
                >
                  {['Outstanding', 'Satisfied', 'Waived']?.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
